import type { RouteObject } from "react-router";

/**
 * The Co-working workspace's routes (M21 W0-C-03, contracts §15), in a file of their own like `labourRoutes.tsx`
 * and `studentsRoutes.tsx` — `index.tsx` mounts this under the shell in one line.
 *
 * One layout route for the whole workspace: `CwkWorkspaceGate` answers the add-on question once (the
 * entitlements, or any Co-working read refused with `402 ADDON_REQUIRED`) and shows `AddonGate` in the page's
 * place. Every page is a file in its owning lane's folder (`src/modules/coworking/<area>/pages/`); a lane
 * replaces the file and never edits this table. Static segments come before the parameter beside them
 * (`members/new` before `members/:personId`, `contracts/new` before `contracts/:contractId`); the two optional
 * segments (`settings/:tab?`, `setup/:step?`) are one route each, their absence being the first tab and the first
 * open step. A tax document of any kind opens at `invoices/:documentId`; there is no `credit-notes/:id`.
 */
export const coworkingRoute: RouteObject = {
  path: "coworking",
  lazy: async () => ({
    Component: (await import("@/modules/coworking/shared/CwkWorkspaceGate")).CwkWorkspaceGate,
  }),
  children: [
    {
      // The morning: thirteen tiles, the task lists, "n of 7" of the setup guide (DSH-001, NTF-001).
      index: true,
      handle: { title: "Dashboard" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/dashboard/pages/CwkDashboardPage")).CwkDashboardPage,
      }),
    },
    {
      // The company directory (CO-001).
      path: "companies",
      handle: { title: "Companies" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/companies/pages/CwkCompaniesPage")).CwkCompaniesPage,
      }),
    },
    {
      // A company: registrations, contracts, seats, balance (CO-002).
      path: "companies/:companyId",
      handle: { title: "Company" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/companies/pages/CwkCompanyPage")).CwkCompanyPage,
      }),
    },
    {
      // The member directory; `new` and the member's page beneath it.
      path: "members",
      handle: { title: "Members" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/members/pages/CwkMembersPage")).CwkMembersPage,
      }),
    },
    {
      // Register a member, or adopt an existing person (MEM-001). Static segment, written BEFORE `:personId`.
      path: "members/new",
      handle: { title: "Register member" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/members/pages/CwkMemberRegisterPage"))
          .CwkMemberRegisterPage,
      }),
    },
    {
      // Keyed on the shared `people.id`: a member IS a person (ADR-0017), so the core person page's "Also in: Co-working" link lands here.
      path: "members/:personId",
      handle: { title: "Member" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/members/pages/CwkMemberPage")).CwkMemberPage,
      }),
    },
    {
      // The contract list with its state and expiry filters.
      path: "contracts",
      handle: { title: "Contracts" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/contracts/pages/CwkContractsPage")).CwkContractsPage,
      }),
    },
    {
      // The draft form (CON-001). Static segment, written BEFORE `:contractId`.
      path: "contracts/new",
      handle: { title: "New contract" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/contracts/pages/CwkContractNewPage"))
          .CwkContractNewPage,
      }),
    },
    {
      // A contract: lines, seats, deposits, documents, notice and end.
      path: "contracts/:contractId",
      handle: { title: "Contract" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/contracts/pages/CwkContractPage")).CwkContractPage,
      }),
    },
    {
      // The operator's own invoices — never Nexborg's (ADR-0019 addendum, item 3).
      path: "invoices",
      handle: { title: "Invoices" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/documents/pages/CwkInvoicesPage")).CwkInvoicesPage,
      }),
    },
    {
      // Any tax document of the module (an invoice, a note, a deposit demand) opens on its invoice address.
      path: "invoices/:documentId",
      handle: { title: "Invoice" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/documents/pages/CwkDocumentPage")).CwkDocumentPage,
      }),
    },
    {
      // Credit and debit notes.
      path: "credit-notes",
      handle: { title: "Credit notes" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/documents/pages/CwkCreditNotesPage"))
          .CwkCreditNotesPage,
      }),
    },
    {
      // Money received, split across invoices, with the TDS recorded beside it.
      path: "payments",
      handle: { title: "Payments" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/payments/pages/CwkPaymentsPage")).CwkPaymentsPage,
      }),
    },
    {
      // Received, released, refunded, held and forfeited, per contract.
      path: "deposits",
      handle: { title: "Deposits" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/deposits/pages/CwkDepositsPage")).CwkDepositsPage,
      }),
    },
    {
      // The receivable by payer and quarter, and the certificates still to come.
      path: "tds",
      handle: { title: "TDS" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/tds/pages/CwkTdsPage")).CwkTdsPage,
      }),
    },
    {
      // The holder picker; the five ledger surfaces of the module live under Statements.
      path: "statements",
      handle: { title: "Statements" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/statements/pages/CwkStatementsPage")).CwkStatementsPage,
      }),
    },
    {
      // One holder's statement: `:kind` is the ledger's own holder kind (`person` or `party`).
      path: "statements/:kind/:id",
      handle: { title: "Statement" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/statements/pages/CwkStatementPage")).CwkStatementPage,
      }),
    },
    {
      // Each day's invoice run, and the preview before it (BIL-001).
      path: "billing",
      handle: { title: "Billing runs" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/billing/pages/CwkBillingRunsPage")).CwkBillingRunsPage,
      }),
    },
    {
      // What a run issued, skipped and warned about.
      path: "billing/:runId",
      handle: { title: "Billing run" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/billing/pages/CwkBillingRunPage")).CwkBillingRunPage,
      }),
    },
    {
      // Accounts that may be blocked for non-payment, and the ones that are (ADR-0018).
      path: "unpaid",
      handle: { title: "Ready to block" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/unpaid/pages/CwkUnpaidPage")).CwkUnpaidPage,
      }),
    },
    {
      // Sell a day pass in a minute, and today's passes.
      path: "day-passes",
      handle: { title: "Day passes" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/dayPasses/pages/CwkDayPassesPage")).CwkDayPassesPage,
      }),
    },
    {
      // Desks, cabins, passes and add-ons with their prices and groups.
      path: "products",
      handle: { title: "Products" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/products/pages/CwkProductsPage")).CwkProductsPage,
      }),
    },
    {
      // The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum).
      path: "reports",
      handle: { title: "Co-working reports" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/reports/pages/CwkReportsPage")).CwkReportsPage,
      }),
    },
    {
      // `:reportId` is the report's address segment; its page chooses the report.
      path: "reports/:reportId",
      handle: { title: "Co-working report" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/reports/pages/CwkReportPage")).CwkReportPage,
      }),
    },
    {
      // The three cut-over files: members, contracts, opening documents (IMP-002).
      path: "import",
      handle: { title: "Import" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/imports/pages/CwkImportPage")).CwkImportPage,
      }),
    },
    {
      // `/coworking/settings[/:tab]` — the tab ids are `COWORKING_SETTINGS_TABS`; the bare address is Centre.
      path: "settings/:tab?",
      handle: { title: "Co-working settings" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/settings/pages/CwkSettingsPage")).CwkSettingsPage,
      }),
    },
    {
      // `/coworking/setup[/:step]` — the step is `1..7`, as `setupFactsDto` numbers them.
      path: "setup/:step?",
      handle: { title: "Setup guide" },
      lazy: async () => ({
        Component: (await import("@/modules/coworking/setup/pages/CwkSetupPage")).CwkSetupPage,
      }),
    },
  ],
};
